import { useState } from 'react';
import { useFormik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { TabsProps } from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material/styles';
import { AddOnCard } from '@components/ProductOffers/configuration/cards/AddOnCard/AddOnCard';
import { CardGrid, CloseButton, ErrorBanner } from '@components/ProductOffers/configuration/steps/shared.styles';
import { ScrollableContent } from '@components/ProductOffers/configuration/steps/TVAddOnsStep/TVAddOnsStep.styles';
import { useEvBundleOfferings } from '@hooks/useEvBundleOfferings/useEvBundleOfferings';
import { useSessionContext } from '@contexts/SessionContext';
import TabPanel from '@shared/components/layout/TabPanel/TabPanel';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import type { Plan } from '@t/productOffering.types';
import { offeringToAddOn, constraintLabel } from '@utils/evBundleDialogUtils';
import type { EvRelationshipOfferings } from '@hooks/useEvBundleOfferings/useEvBundleOfferings';
import { buildValidationSchema } from './EvBundleRelationshipDialog.schema';

type EvBundleRelationshipDialogProps = {
  plan: Plan | null;
  onConfirm: (mainPlan: Plan, selectedOfferings: Plan[]) => Promise<void>;
  onDismiss: () => void;
  isConfirming?: boolean;
  /** Offering IDs already selected — used to pre-populate the dialog when editing an existing plan. */
  initialSelectedIds?: string[];
};

// selectionsByTab is flat: Record<relationshipId, planId[]>
type FormValues = Record<string, string[]>;

// MUI types `Tabs.variant` as a closed union, so declaration merging cannot add
// `addOnTabs` to it (TS2717, see theme.declarations.ts). The theme variant still
// matches at runtime — MUI resolves `variants[]` against `ownerState` — so the
// name is narrowed here rather than registered in an override interface.
const ADD_ON_TABS_VARIANT = 'addOnTabs' as TabsProps['variant'];

const buildInitialValues = (
  groups: EvRelationshipOfferings[],
  initialSelectedIds?: string[]
): FormValues => {
  if (!initialSelectedIds?.length) return {};
  const result: FormValues = {};
  for (const group of groups) {
    const seeds = group.offerings
      .filter((o) => initialSelectedIds.includes(o.planId))
      .map((o) => o.planId);
    if (seeds.length > 0) result[group.relationship.id] = seeds;
  }
  return result;
};

export const EvBundleRelationshipDialog = ({
  plan,
  onConfirm,
  onDismiss,
  isConfirming = false,
  initialSelectedIds,
}: EvBundleRelationshipDialogProps) => {
  const { sessionId } = useSessionContext();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const rels = plan?.bundleRelationships ?? [];
  const relationshipGroups = useEvBundleOfferings(sessionId ?? '', rels);
  const isAnyLoading = relationshipGroups.some((g) => g.isLoading);
  const totalCount = relationshipGroups.reduce((sum, g) => sum + g.offerings.length, 0);

  const validationSchema = buildValidationSchema(relationshipGroups);

  const formik = useFormik<FormValues>({
    // Reinitialize once offerings load so initialSelectedIds can be seeded into the form.
    enableReinitialize: true,
    initialValues: isAnyLoading ? {} : buildInitialValues(relationshipGroups, initialSelectedIds),
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      if (!plan) return;
      const allSelected = relationshipGroups.flatMap((g) => {
        const selected = new Set(values[g.relationship.id] ?? []);
        return g.offerings.filter((o) => selected.has(o.planId));
      });
      await onConfirm(plan, allSelected);
      resetForm();
      setActiveTab(0);
    },
  });

  const handleToggle = (relId: string, planId: string, maxSelect: number) => {
    const current = new Set<string>(formik.values[relId] ?? []);
    if (current.has(planId)) {
      current.delete(planId);
    } else {
      if (maxSelect === 1) {
        current.clear();
      } else if (maxSelect > 1 && current.size >= maxSelect) {
        return;
      }
      current.add(planId);
    }
    void formik.setFieldValue(relId, Array.from(current));
  };

  const handleClose = () => {
    formik.resetForm();
    setActiveTab(0);
    onDismiss();
  };

  const getSelected = (relId: string): Set<string> =>
    new Set<string>(formik.values[relId] ?? []);

  const hasErrors = formik.submitCount > 0 && Object.keys(formik.errors).length > 0;
  const firstFailingGroup = hasErrors
    ? relationshipGroups.find(
        (g) => g.minSelect > 0 && (formik.values[g.relationship.id]?.length ?? 0) < g.minSelect
      )
    : undefined;

  const totalSelected = relationshipGroups.reduce(
    (sum, g) => sum + getSelected(g.relationship.id).size,
    0
  );

  return (
    <Dialog open={!!plan} onClose={handleClose} variant="configurePlanDialog">
      <DialogTitle>
        <Typography variant="title.step" className="StepHeader-title">
          Enhance Your EV Plan
        </Typography>
        <CloseButton onClick={handleClose}>
          <CloseIcon variant="dialogCloseIcon" />
        </CloseButton>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 1 }}
      >
        <Stack spacing={0.5} useFlexGap sx={{ mb: 1.5 }}>
          <Typography variant="heading.subsection">Select your preference</Typography>
          <Typography variant="body.md" color={theme.novus.borderBlack}>
            {`Your plan works with ${totalCount} option${totalCount !== 1 ? 's' : ''} you might like.`}
          </Typography>
        </Stack>

        {relationshipGroups.length > 1 && (
          <Tabs
            value={activeTab}
            onChange={(_: React.SyntheticEvent, v: number) => setActiveTab(v)}
            variant={ADD_ON_TABS_VARIANT}
          >
            {relationshipGroups.map((g) => (
              <Tab key={g.relationship.id} label={g.relationship.name} variant="addOnTab" />
            ))}
          </Tabs>
        )}

        {hasErrors && firstFailingGroup && (
          <ErrorBanner>
            <ErrorOutlineIcon variant="errorBannerIcon" />
            <Typography variant="label.field" color={theme.novus.errorRed}>
              {`Please select at least ${firstFailingGroup.minSelect} option${firstFailingGroup.minSelect > 1 ? 's' : ''} from "${firstFailingGroup.relationship.name}"`}
            </Typography>
          </ErrorBanner>
        )}

        <ScrollableContent>
          {isAnyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            relationshipGroups.map((g, i) => {
              const selected = getSelected(g.relationship.id);
              const hint = constraintLabel(g.minSelect, g.maxSelect);
              return (
                <TabPanel key={g.relationship.id} value={activeTab} index={i}>
                  {hint && (
                    <Typography variant="body.sm" color={theme.novus.borderBlack} sx={{ px: '24px', pt: '12px' }}>
                      {hint}
                    </Typography>
                  )}
                  <CardGrid sx={{ p: '24px' }}>
                    {g.offerings.map((offering) => (
                      <AddOnCard
                        key={offering.planId}
                        addOn={offeringToAddOn(offering)}
                        isSelected={selected.has(offering.planId)}
                        quantity={1}
                        onToggle={() => handleToggle(g.relationship.id, offering.planId, g.maxSelect)}
                        onQuantityChange={() => {}}
                      />
                    ))}
                  </CardGrid>
                </TabPanel>
              );
            })
          )}
        </ScrollableContent>
      </DialogContent>

      <DialogActions>
        <Typography variant="body.md" className="AddOnsFooter-summary">
          {totalSelected > 0 ? (
            <>
              <Box component="span" fontWeight={700}>{totalSelected}</Box>
              {` option${totalSelected !== 1 ? 's' : ''} selected`}
            </>
          ) : (
            'No option selected'
          )}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap>
          <Button variant="outlined" onClick={handleClose} disabled={isConfirming}>
            <Typography variant="label.button" color="inherit">
              Skip
            </Typography>
          </Button>
          <LoadingButton
            variant="contained"
            onClick={() => void formik.submitForm()}
            loading={isConfirming}
          >
            <Typography variant="label.button" color="inherit">
              Confirm
            </Typography>
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
